import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { requestActAsTokens, revokeActAsSession } from '../../stores/ActAsSession';
import {
    enterActAsTokenOverlay,
    exitActAsTokenOverlay,
    getRefreshToken,
    isActAsSessionActive
} from '../../utilities/storage/tokenStorage';
import { runStorageSwitch } from '../../utilities/storage/storageDomains';
import { clearActAsMarker, setActAsMarker } from '../../utilities/storage/actAsMarkerStorage';
import type Users from '../../schemas/public/Users';

interface ActAsModeDeps {
    refreshUser: () => Promise<Users | null>;
    /** Session d'activité stats (useAppSession) : à clore/rouvrir de part et d'autre de la bascule. */
    endAppSession: () => Promise<void>;
    startAppSession: () => Promise<void>;
}

/** Entrée/sortie du mode accompagné : bascule d'identité SANS toucher à la session du Pro. */
export function useActAsMode({ refreshUser, endAppSession, startAppSession }: ActAsModeDeps) {
    const history = useHistory();
    const { t } = useTranslation();

    const enterActAs = useCallback(
        async (beneficiaryId: string): Promise<boolean> => {
            if (isActAsSessionActive()) return false; // pas d'act-as en chaîne (garde serveur aussi)
            const tokens = await requestActAsTokens(beneficiaryId);
            if (!tokens) return false; // état du Pro intact : rien n'a encore basculé
            await endAppSession(); //       clôt la session d'activité du Pro
            await runStorageSwitch(); //    purge app_session / route_activity
            enterActAsTokenOverlay(tokens);
            const beneficiary = await refreshUser(); // charge le bénéficiaire (jeton overlay)
            // Marqueur pour le toast au redémarrage : raconte l'accompagnement, ne le reprend pas.
            await setActAsMarker({ beneficiaryFirstName: beneficiary?.firstName ?? '' });
            await startAppSession(); //     ouvre SA session d'activité
            history.replace('/home');
            toast.success(
                t("Vous accompagnez maintenant {{name}}.", {
                    name: beneficiary?.firstName ?? ''
                })
            );
            return true;
        },
        [refreshUser, endAppSession, startAppSession, history, t]
    );

    const exitActAs = useCallback(async (): Promise<void> => {
        if (!isActAsSessionActive()) return;
        await endAppSession(); //           clôt la session du bénéficiaire (overlay encore actif)
        const actAsRefreshToken = await getRefreshToken();
        exitActAsTokenOverlay();
        await runStorageSwitch();
        await clearActAsMarker(); // sortie propre : rien à raconter au prochain démarrage
        if (actAsRefreshToken) revokeActAsSession(actAsRefreshToken);
        await refreshUser(); //             recharge le Pro
        await startAppSession();
        history.replace('/home'); // décision 14 : le choix suivant se fait via la pilule du panel
        toast.info(t('Vous êtes revenu sur votre espace pro.'));
    }, [refreshUser, endAppSession, startAppSession, history, t]);

    return { enterActAs, exitActAs };
}
